import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, ChevronDown } from "lucide-react";
import { api, apiError, cn, publicAppUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import WhatsAppShareButton, { SIGN_OFF } from "./WhatsAppShareButton";
import { withUnit, drawClubCard, clubCaption } from "../lib/reportImage";
import InstagramShareSheet, { InstagramButton } from "./InstagramShareSheet";
import { Card, Empty, Skeleton } from "./ui";
import { Avatar } from "../pages/MembersPage";

const PERIODS = [
  { key: "all", label: "All time" },
  { key: "year", label: "This year" },
  { key: "month", label: "This month" },
];

const BOARD_EMOJI = {
  mvp: "🏆", batting: "🏏", bowling: "🎯", fielding: "🧤", captain: "👑", sixes: "💥", attendance: "📅",
};
const MEDALS = ["🥇", "🥈", "🥉"];
// "1 run", "2 runs" in the shared message
export { withUnit } from "../lib/reportImage";
const SHOW = 3;

/** Club performance: who's on top for batting, bowling, fielding, captaincy… */
export default function ClubLeaderboards() {
  const { club } = useAuth();
  const [period, setPeriod] = useState("all");
  const [igOpen, setIgOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get("/stats/club", { params: { period } })
      .then(({ data }) => alive && setData(data))
      .catch((err) => { toast.error(apiError(err)); alive && setData({ boards: [], matches: 0 }); });
    return () => { alive = false; };
  }, [period]);

  const boards = (data?.boards || []).filter((b) => b.rows.length > 0);

  const shareText = () => {
    const lines = [
      `🏆 *${club?.name || "Club"} — Club performance*`,
      [
        PERIODS.find((p) => p.key === period).label,
        `${data.matches} match${data.matches === 1 ? "" : "es"}`,
        data.match_days > 0 && `${data.match_days} match day${data.match_days === 1 ? "" : "s"}`,
      ].filter(Boolean).join(" • "),
    ];
    if (data.totals) {
      const t = data.totals;
      lines.push("", `📈 ${t.runs} runs • ${t.wickets} wickets • ${t.sixes} sixes • ${t.fours} fours • ${t.catches} catches`);
    }
    boards.forEach((b) => {
      lines.push("", `${BOARD_EMOJI[b.key]} *${b.title}*`);
      b.rows.slice(0, 3).forEach((r, i) => lines.push(`${MEDALS[i]} ${r.name} — ${withUnit(r.value, b.unit)}`));
    });
    lines.push("", `📊 Full leaderboards: ${publicAppUrl()}/stats?tab=club`, ...SIGN_OFF);
    return lines.join("\n");
  };

  return (
    <div className="px-5 space-y-4" data-testid="club-leaderboards">
      <div className="grid grid-cols-3 gap-1 p-1 bg-black/[0.04] rounded-xl text-xs font-semibold" role="tablist">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            role="tab"
            aria-selected={period === p.key}
            onClick={() => setPeriod(p.key)}
            className={cn("h-9 rounded-lg", period === p.key ? "bg-white shadow-sm text-ink" : "text-ink-muted")}
            data-testid={`club-period-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="space-y-3"><Skeleton className="h-44" /><Skeleton className="h-44" /></div>
      ) : boards.length === 0 ? (
        <Card className="p-0">
          <Empty icon={BarChart3} title="No stats yet" subtitle="Leaderboards fill up as matches are scored." />
        </Card>
      ) : (
        <>
          <div className="text-xs text-ink-muted -mt-1">
            From {data.matches} completed match{data.matches === 1 ? "" : "es"}
            {data.match_days > 0 && ` on ${data.match_days} match day${data.match_days === 1 ? "" : "s"}`} • guests not ranked
          </div>
          {data.totals && <TotalsGrid t={data.totals} />}
          <div className="grid grid-cols-2 gap-2">
            <WhatsAppShareButton getText={shareText} label="WhatsApp" data-testid="share-club-stats" />
            <InstagramButton onClick={() => setIgOpen(true)} size="lg" data-testid="share-club-stats-ig" />
          </div>
          <InstagramShareSheet
            open={igOpen}
            onClose={() => setIgOpen(false)}
            title="Share club performance"
            makeImage={() => drawClubCard({ clubName: club?.name, periodLabel: PERIODS.find((p) => p.key === period).label, data })}
            caption={clubCaption({ clubName: club?.name, periodLabel: PERIODS.find((p) => p.key === period).label, data })}
            fileName={`cricso-${club?.name || "club"}-leaderboards`}
          />
          {boards.map((b) => <Board key={b.key} b={b} me={data.me} />)}
        </>
      )}
    </div>
  );
}

const TOTALS = [
  { key: "runs", label: "Runs", emoji: "🏏" },
  { key: "wickets", label: "Wickets", emoji: "🎯" },
  { key: "sixes", label: "Sixes", emoji: "💥" },
  { key: "fours", label: "Fours", emoji: "4️⃣" },
  { key: "catches", label: "Catches", emoji: "🧤" },
  { key: "run_outs", label: "Run outs", emoji: "🏃" },
];

function TotalsGrid({ t }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="club-totals">
      {TOTALS.map(({ key, label, emoji }) => (
        <Card key={key} className="p-3 text-center">
          <div className="text-xl font-extrabold tabular-nums leading-tight">{t[key] ?? 0}</div>
          <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mt-0.5">
            <span aria-hidden>{emoji}</span> {label}
          </div>
        </Card>
      ))}
    </div>
  );
}

function Board({ b, me }) {
  const [open, setOpen] = useState(false);
  const rows = open ? b.rows : b.rows.slice(0, SHOW);
  const myRank = b.rows.findIndex((r) => r.member_id === me);

  return (
    <Card className="p-0 overflow-hidden" data-testid={`board-${b.key}`}>
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-bold flex items-center gap-1.5">
            <span aria-hidden>{BOARD_EMOJI[b.key]}</span> {b.title}
          </div>
          {b.hint && <div className="text-[11px] text-ink-muted">{b.hint}</div>}
        </div>
        {myRank >= SHOW && !open && (
          <span className="text-[11px] font-semibold text-brand shrink-0 pt-1">You're #{myRank + 1}</span>
        )}
      </div>

      <ol className="divide-y divide-black/[0.05]">
        {rows.map((r, i) => (
          <li
            key={r.member_id}
            className={cn("flex items-center gap-3 px-4 py-2.5", i === 0 && "bg-amber-50/60", r.member_id === me && "bg-brand-50/70")}
          >
            <span className="w-6 text-center shrink-0 text-sm font-bold text-ink-muted tabular-nums">
              {MEDALS[i] || i + 1}
            </span>
            <Avatar member={r} size={32} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">
                {r.name}{r.member_id === me && <span className="text-[10px] font-bold text-brand ml-1.5">YOU</span>}
              </div>
              <div className="text-[11px] text-ink-muted truncate">{r.line}</div>
            </div>
            <div className="text-right shrink-0">
              <div className={cn("font-extrabold tabular-nums leading-tight", i === 0 ? "text-lg" : "text-base")}>{r.value}</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">{b.unit}</div>
            </div>
          </li>
        ))}
      </ol>

      {b.rows.length > SHOW && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="w-full h-10 border-t border-black/[0.05] text-xs font-semibold text-brand flex items-center justify-center gap-1"
        >
          {open ? "Show top 3" : `Show top ${b.rows.length}`}
          <ChevronDown size={14} className={cn("transition-transform", open && "rotate-180")} />
        </button>
      )}
    </Card>
  );
}
